import { Box, Container, Grid, Typography, Accordion, AccordionSummary, AccordionDetails, Paper, Chip, useTheme, alpha, Stack, Button } from '@mui/material';
import Seo from '../components/Seo';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import PlanCalculator from '../components/PlanCalculator';
import SectionHeading from '../components/ui/SectionHeading';
import Reveal from '../components/ui/Reveal';

const examples = [
  { name: 'Small ISP', items: ['Internet €50', 'Fiscalization €30'], total: 80 },
  { name: 'Most Popular', items: ['Internet €50', 'IPTV €50', 'Fiscalization €30'], total: 130, highlight: true },
  { name: 'Full TV stack', items: ['Internet €50', 'IPTV €50', 'DVB-C €50', 'Fiscalization €30'], total: 180 },
  { name: 'Radius ISP', items: ['SolvendaRadius €80', 'Fiscalization €30'], total: 110 },
  { name: 'Radius + IPTV', items: ['SolvendaRadius €80', 'IPTV €50', 'Fiscalization €30'], total: 160 },
  { name: 'Network operator', items: ['Internet €50', 'IPTV €50', 'Fiscalization €30', '3 OLTs ×€30'], total: 220 },
];

const faqs = [
  {
    q: 'Why is SolvendaRadius €80 and not €50 + €80?',
    a: 'SolvendaRadius replaces the normal Internet module — it is your internet engine. You pick either Solvenda Internet (€50) or SolvendaRadius (€80), not both. Only rare ISPs running two separate networks would pay for both.',
  },
  {
    q: 'How are SolvendaConnect fees charged?',
    a: 'Connect has no monthly fee. We charge per successful card payment: 50 ALL flat for payments under 5,000 ALL, and 2% + 20 ALL for payments of 5,000 ALL and above. Subscriptions are billed in EUR; Connect fees are in ALL.',
  },
  {
    q: 'Is there a setup fee?',
    a: 'No. There is no setup fee for now — you only pay the monthly price of the modules you use, plus Connect transaction fees if you enable online payments.',
  },
  {
    q: 'Can I combine multiple services?',
    a: 'Yes. Add IPTV and DVB-C to either engine, and bolt on Fiscalization, OLT Management and Connect as needed. Use the calculator above to see your exact monthly total.',
  },
  {
    q: 'How is OLT Management priced?',
    a: 'OLT Management is €30/month for each configured OLT device. ZTE C320, C300 and C600 are supported today, with Huawei coming soon.',
  },
  {
    q: 'Do you offer an all-in-one option?',
    a: 'Yes. The All-in-One license unlocks every module — Core, SolvendaRadius, Fiscalization, OLT and Connect. Contact us for All-in-One pricing tailored to your ISP.',
  },
];

const PricingPage = () => {
  const theme = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ pt: { xs: 12, md: 16 }, pb: { xs: 8, md: 12 } }}>
      <Seo
        title="SOLVENDA Pricing | Modular ISP Software from €50/month — Build Your Plan"
        description="Transparent, modular pricing for SOLVENDA. Solvenda Internet €50, SolvendaRadius €80, IPTV/DVB-C €50, Fiscalization €30, DNS €15, Logs History €15, OLT €30 per device, SolvendaConnect free with per-transaction fees. No setup fee — build your plan instantly."
        keywords="ISP management pricing, FreeRadius pricing, OLT management cost, fiscalization Albania price, DNS filtering price, log retention pricing, online payment fees, ISP software cost"
        path="/pricing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />

      <Container>
        <SectionHeading
          overline="Pricing"
          title="Build your plan"
          subtitle="Pick your engine, add services and add-ons, and see your monthly total instantly. No setup fee."
        />

        <Box id="calculator" sx={{ scrollMarginTop: 100, mb: { xs: 8, md: 12 } }}>
          <PlanCalculator />
        </Box>

        {/* Worked examples */}
        <SectionHeading
          overline="Real examples"
          title="Plans ISPs actually buy"
          subtitle="A few common combinations and their monthly totals."
        />
        <Grid container spacing={3} sx={{ mb: { xs: 8, md: 12 } }}>
          {examples.map((ex, i) => (
            <Grid item xs={12} sm={6} md={4} key={ex.name}>
              <Reveal delay={i * 50} sx={{ height: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: 3,
                    position: 'relative',
                    border: '1px solid',
                    borderColor: ex.highlight ? alpha(theme.palette.primary.main, 0.5) : 'divider',
                    bgcolor: ex.highlight ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
                  }}
                >
                  {ex.highlight && (
                    <Chip
                      icon={<StarRoundedIcon sx={{ fontSize: 16 }} />}
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{ position: 'absolute', top: 16, right: 16 }}
                    />
                  )}
                  <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                    {ex.name}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mb: 2 }}>
                    {ex.items.map((it) => (
                      <Typography key={it} variant="body2" color="text.secondary">
                        {it}
                      </Typography>
                    ))}
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 800 }}>
                      €{ex.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      /month
                    </Typography>
                  </Box>
                </Paper>
              </Reveal>
            </Grid>
          ))}
        </Grid>

        {/* FAQ */}
        <SectionHeading overline="FAQ" title="Frequently asked questions" />
        <Box sx={{ maxWidth: 820, mx: 'auto', mb: { xs: 8, md: 10 } }}>
          {faqs.map((f) => (
            <Accordion
              key={f.q}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: '12px !important',
                border: '1px solid',
                borderColor: 'divider',
                '&::before': { display: 'none' },
                bgcolor: 'background.paper',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {f.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {f.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* CTA */}
        <Reveal>
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            }}
          >
            <Typography variant="h3" component="h2" sx={{ mb: 2 }}>
              Ready to streamline your ISP?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              No setup fee, no lock-in. Tell us what you run and we will get you live.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button component={Link} to="/contact" variant="contained" color="primary" size="large">
                Contact us
              </Button>
              <Button component={Link} to="/features" variant="outlined" color="primary" size="large">
                Explore features
              </Button>
            </Stack>
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
};

export default PricingPage;
